'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { TbBrandHipchat, TbX } from 'react-icons/tb';

import { TrakdChatPanel } from './trakd-chat-panel';

interface WidgetMessage {
  role: string;
  content: string;
  createdAt: string;
}

export interface TrakdChatWidgetProps {
  initialMessages?: WidgetMessage[];
  userName?: string | null;
  variant?: 'app' | 'public';
}

/**
 * Floating chat widget · canto inferior direito do viewport.
 *
 * - Botão circular SEMPRE visível, alternando hipchat ↔ X com morph (rotate + blur)
 * - Painel sobe com spring (scale + translateY + blur) ancorado no canto inferior direito
 * - ESC fecha · click fora fecha
 */
export function TrakdChatWidget({
  initialMessages = [],
  userName = null,
  variant = 'app',
}: TrakdChatWidgetProps): ReactElement | null {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  // Abre o chat quando qualquer parte do app dispara `trakd:open-chat`
  // (ex.: card "Perguntar à IA do Trakd" no hub de suporte).
  useEffect(() => {
    function onOpen(e: Event): void {
      const detail = (e as CustomEvent<{ prefill?: string }>).detail;
      if (detail && typeof detail.prefill === 'string') setPrefill(detail.prefill);
      setOpen(true);
    }
    window.addEventListener('trakd:open-chat', onOpen);
    return (): void => window.removeEventListener('trakd:open-chat', onOpen);
  }, []);

  // ESC fecha
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        setOpen(false);
        setPrefill(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return (): void => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Click fora fecha
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent): void {
      const target = e.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
      setPrefill(null);
    }
    document.addEventListener('mousedown', onMouseDown);
    return (): void => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <>
      {/* Painel — sobe com spring + blur (estilo Floating Action Button do motion.dev) */}
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            data-floating
            initial={{ opacity: 0, scale: 0.85, filter: 'blur(10px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.85, filter: 'blur(8px)' }}
            transition={{ type: 'spring', stiffness: 260, damping: 26, mass: 0.9 }}
            style={{
              position: 'fixed',
              right: '24px',
              bottom: '96px',
              zIndex: 9999,
              transformOrigin: 'calc(100% - 28px) 100%',
            }}
          >
            <TrakdChatPanel
              initialMessages={initialMessages}
              userName={userName}
              variant={variant}
              initialDraft={prefill ?? undefined}
              onClose={() => {
                setOpen(false);
                setPrefill(null);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Botão floating · ícone morph (rotate + scale + blur) */}
      <button
        ref={buttonRef}
        data-floating
        data-tour="chat-button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Fechar chat' : 'Abrir chat com assistente Trakd'}
        aria-expanded={open}
        style={{ position: 'fixed', right: '24px', bottom: '24px', zIndex: 9999 }}
        className="bg-trakd-fill shadow-trakd-glow-violet group text-on-brand relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full transition-transform hover:scale-105 active:scale-95"
      >
        <span
          aria-hidden
          className="bg-trakd-brand pointer-events-none absolute inset-[-10px] -z-10 rounded-full opacity-35 blur-2xl transition-opacity group-hover:opacity-50"
        />
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span
              key="x"
              initial={{ rotate: -90, scale: 0.4, opacity: 0, filter: 'blur(6px)' }}
              animate={{ rotate: 0, scale: 1, opacity: 1, filter: 'blur(0px)' }}
              exit={{ rotate: 90, scale: 0.4, opacity: 0, filter: 'blur(6px)' }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <TbX size={22} strokeWidth={2.2} />
            </motion.span>
          ) : (
            <motion.span
              key="chat"
              initial={{ rotate: 90, scale: 0.4, opacity: 0, filter: 'blur(6px)' }}
              animate={{ rotate: 0, scale: 1, opacity: 1, filter: 'blur(0px)' }}
              exit={{ rotate: -90, scale: 0.4, opacity: 0, filter: 'blur(6px)' }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <TbBrandHipchat size={24} strokeWidth={1.8} />
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </>,
    document.body,
  );
}
